import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  Bot,
  Captions,
  Clapperboard,
  Image,
  MicVocal,
  Sparkles,
  UserRound,
  Video,
  type LucideIcon,
} from 'lucide-react'
import { DemoVideosSection } from './digital-human-canvas'

type CreationTool = {
  title: string
  description: string
  to:
    | '/app/tools/video-gen'
    | '/app/tools/digital-human'
    | '/app/tools/image-gen'
    | '/app/tools/voiceover'
    | '/app/tools/voice-clone'
    | '/app/tools/transcription'
  icon: LucideIcon
}

const tools: CreationTool[] = [
  { title: 'AI 配音', description: '生成旁白音频', to: '/app/tools/voiceover', icon: MicVocal },
  { title: '声音克隆', description: '复用专属音色', to: '/app/tools/voice-clone', icon: Bot },
  {
    title: '字幕与转写',
    description: '提取并编辑字幕',
    to: '/app/tools/transcription',
    icon: Captions,
  },
  { title: 'AI 生图', description: '生成封面与场景', to: '/app/tools/image-gen', icon: Image },
  { title: 'AI 生成视频', description: '从创意生成片段', to: '/app/tools/video-gen', icon: Video },
  { title: '数字人', description: '生成讲解视频', to: '/app/tools/digital-human', icon: UserRound },
]

export function AiCreationCanvas() {
  return (
    <div className="mx-auto w-full max-w-[720px] px-5 pb-16 pt-12 sm:px-8 sm:pt-16">
      <header className="text-center">
        <div className="mx-auto flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Sparkles className="size-5" />
        </div>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          从一个想法开始创作
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">生成内容后，随时进入项目继续编辑。</p>
      </header>

      <section className="mt-8">
        <Link
          to="/projects/new"
          search={{ assistant: true }}
          className="group flex items-center gap-4 rounded-2xl bg-card px-5 py-4 transition-colors hover:bg-accent"
        >
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Clapperboard className="size-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
              口播视频草稿
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                推荐
              </span>
            </span>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">
              主题或文案 → 脚本、云端配音、字幕与背景音乐 → 可编辑项目
            </span>
          </span>
          <ArrowRight className="size-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5" />
        </Link>
      </section>

      <div className="my-9 flex items-center gap-4 text-xs text-muted-foreground before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
        更多工具
      </div>

      <section className="grid grid-cols-3 gap-y-7 sm:grid-cols-6">
        {tools.map((tool) => {
          const Icon = tool.icon
          return (
            <Link
              key={tool.to}
              to={tool.to}
              className="group flex min-w-0 flex-col items-center text-center"
            >
              <span className="flex size-12 items-center justify-center rounded-full bg-card text-foreground transition-all group-hover:-translate-y-0.5 group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon className="size-5" />
              </span>
              <span className="mt-2.5 truncate text-xs font-medium text-foreground">
                {tool.title}
              </span>
              <span className="mt-0.5 hidden text-[10px] text-muted-foreground sm:block">
                {tool.description}
              </span>
            </Link>
          )
        })}
      </section>

      <section className="mt-12 border-t border-border pt-8">
        <DemoVideosSection
          title="AI 创作效果演示"
          className="relative left-1/2 w-[calc(100vw-3rem)] max-w-[1000px] -translate-x-1/2 sm:w-[calc(100vw-5rem)]"
        />
      </section>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        生成内容可保存到素材库或继续编辑。
      </p>
    </div>
  )
}
